import { PublicEWebinarFragment, RoomType } from '$/GraphQL/schema/schema';
import formatSeconds from '$/utils/formatSeconds';
import React from 'react';

import {
  WebinarPlayerContext,
  WebinarPlayerUIState,
  withWebinarPlayerContext,
} from '$/components/WebinarPlayer/context';
import { Trans } from '@lingui/macro';
import { css } from '@emotion/react';
import tinycolor from 'tinycolor2';
import { TitleAndPresenters } from '../Controls/TitleAndPresenters';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { TimeLeftToDate } from '$/utils/TimeLeftToDate';
import tw from 'twin.macro';
import { RoomPartsBase, CustomerLogoFooter } from './RoomPartsBase';
import { withContextValue } from '$/hooks/withPartialContexts';
import { isMobile } from '$/utils/isMobile';

export const useWaitingRoomGradient = (ewebinar: PublicEWebinarFragment): string => {
  let darkPrimaryColor = tinycolor(ewebinar.primaryColor);
  if (darkPrimaryColor.getLuminance() > 1 - 0.075) {
    darkPrimaryColor = darkPrimaryColor.clone().darken(0);
  }

  let darkActionColor = tinycolor(ewebinar.actionColor);
  if (darkActionColor.getLuminance() > 1 - 0.075) {
    darkActionColor = darkPrimaryColor.clone().darken(0);
  }

  return `
    background: ${ewebinar.primaryColor};
    background: linear-gradient(45deg, ${darkPrimaryColor.toHexString()} 0%, ${darkActionColor.toHexString()} 100%);
    color: #fff;
  `;
};

export const WaitingRoomVideoStage = withContextValue<
  WebinarPlayerUIState,
  { timerRestartKey: number },
  { timerRestartKey: number }
>(WebinarPlayerContext, ({ isPlaying, playbackPosition }, {}, oldValue) => {
  const playbackPositionRef = React.useRef(playbackPosition);
  if (isPlaying && oldValue) {
    const acceptableDeltaSecs = 7;
    const useNewKey =
      Math.abs(playbackPosition - playbackPositionRef.current) > acceptableDeltaSecs;
    playbackPositionRef.current = playbackPosition;
    if (!useNewKey) return oldValue;
  }
  playbackPositionRef.current = playbackPosition;
  return { timerRestartKey: playbackPosition };
})(
  withWebinarPlayerContext(
    ['rooms', 'playbackPositionValue', 'currentRoom', 'attendee', 'isPlaying', 'ewebinar'],
    'WaitingRoomVideoStage'
  )(({ timerRestartKey, context }) => {
    const { rooms, playbackPositionValue, currentRoom, attendee, isPlaying, ewebinar } = context;
    const gradient = useWaitingRoomGradient(ewebinar);

    const waitingRoomDuration = rooms.getRoom(RoomType.Waiting).duration;
    const diameterPx = isMobile ? 310 : 500;

    const renderTime = React.useMemo(() => {
      return ({ remainingTime }: { remainingTime: number }): JSX.Element | null => {
        remainingTime = Math.max(0, remainingTime);
        return (
          <div
            className='relative flex flex-col justify-end items-center'
            css={css`
              .message {
                ${tw`text-2xl md:text-4xl leading-normal text-center`}
                max-width: ${diameterPx * 0.75}px;
              }
              .number {
                ${tw`font-mono`}
                font-size: ${isMobile ? '4' : '8'}rem;
                line-height: initial;
              }
              .unit {
                line-height: initial;
              }
            `}
          >
            <div className='message'>
              <Trans>Your session starts in</Trans>
            </div>
            {currentRoom.type === RoomType.PreWaiting ? (
              <div
                css={css`
                  margin-top: 1rem;
                  span {
                    font-size: 40px;
                    margin-right: 1rem;
                  }
                `}
              >
                <TimeLeftToDate
                  date={attendee?.startTime ? new Date(attendee.startTime) : new Date()}
                  lang={ewebinar.viewerRoomSettings.language ?? 'en'}
                />
              </div>
            ) : (
              <div className='number'>{formatSeconds(remainingTime, true)}</div>
            )}
          </div>
        );
      };
    }, [attendee?.startTime, currentRoom.type, diameterPx, ewebinar.viewerRoomSettings.language]);

    return (
      <div
        id='WaitingRoom.VideoStage.Gradient'
        className='flex w-full h-full'
        css={css`
          ${gradient};
        `}
      >
        <TitleAndPresenters />

        <div
          className='aspect-scale'
          css={css`
            transform-origin: top left;

            position: absolute;
            top: 66%;
            left: 50%;
          `}
        >
          <div
            // Leave room for title +1rem
            css={css`
              transform: translate(-50%, calc(-66% + 1rem));
            `}
          >
            <CountdownCircleTimer
              key={timerRestartKey} // Key is required so a new initialRemainingTime will re-init the component
              isPlaying={isPlaying}
              duration={waitingRoomDuration}
              initialRemainingTime={-playbackPositionValue.get()}
              strokeWidth={15}
              trailStrokeWidth={15}
              strokeLinecap='square'
              size={diameterPx}
              rotation='counterclockwise'
              colors={
                currentRoom.type === RoomType.PreWaiting
                  ? ['#ffffff00', '#ffffff00']
                  : ['#fff', '#fff']
              }
              trailColor={currentRoom.type === RoomType.PreWaiting ? '#ffffff00' : '#ffffff80'}
              colorsTime={[waitingRoomDuration, 0]}
              updateInterval={0.2}
            >
              {renderTime}
            </CountdownCircleTimer>
          </div>
        </div>
      </div>
    );
  })
);

export class WaitingRoomsParts extends RoomPartsBase {
  public footerComponent(): React.FC | null {
    return CustomerLogoFooter;
  }

  public stageComponent(): { component: React.FC; zIndex?: number } | null {
    return { component: WaitingRoomVideoStage, zIndex: 600 };
  }
}

export class PreWatingRoomParts extends RoomPartsBase {
  public footerComponent(): React.FC | null {
    return CustomerLogoFooter;
  }

  public stageComponent(): { component: React.FC; zIndex?: number } | null {
    return { component: WaitingRoomVideoStage };
  }
}
